// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import { dispatch } from '../index';
import * as enrollmentAPI from 'api/enrollment';

export const ENROLLMENT_STATUS = {
    NOT_ENROLLED: 'NOT_ENROLLED',
    ENROLLED: 'ENROLLED',
    DISENROLLED: 'DISENROLLED'
};

const initialState = {
    ccm: {
        error: false,
        loaded: false,
        existingEnrollmentStatus: ENROLLMENT_STATUS.NOT_ENROLLED,
        data: {
            type: 'ccm',
            enrollmentDate: null,
            cancellationDate: null,
            verbalAgreement: false,
            enrolledSameDayOfficeVisit: false,
            primaryClinician: null,
            specialist: null
        }
    },
    hrm: {
        error: false,
        loaded: false,
        existingEnrollmentStatus: ENROLLMENT_STATUS.NOT_ENROLLED,
        data: {
            type: 'hrm',
            enrollmentDate: null,
            cancellationDate: null,
            verbalAgreement: false,
            primaryClinician: null,
            specialist: null
        }
    },
    rpm: {
        error: false,
        loaded: false,
        existingEnrollmentStatus: ENROLLMENT_STATUS.NOT_ENROLLED,
        data: {
            type: 'rpm',
            enrollmentDate: null,
            cancellationDate: null,
            verbalAgreement: false,
            primaryClinician: null,
            specialist: null,
            equipmentSetupAndEducation: null
        }
    },
    currentEnrollments: {
        error: false,
        loaded: false,
        data: {
            ccm: false,
            hrm: false,
            rpm: false
        }
    },
    enrollmentDocumentStatus: {
        error: false,
        loaded: false,
        data: {
            infoDocStatus: null,
            agreementDocStatus: null,
            infoDocFile: null,
            agreementDocFile: null
        }
    }
};

function deriveEnrollmentStatus(payload) {
    if (!payload?.id) {
        return ENROLLMENT_STATUS.NOT_ENROLLED;
    }
    if (payload.cancellationDate) {
        return ENROLLMENT_STATUS.DISENROLLED;
    }
    return ENROLLMENT_STATUS.ENROLLED;
}

const slice = createSlice({
    name: 'enrollment',
    initialState,
    reducers: {
        setCCMEnrollmentError(state) {
            state.ccm.error = true;
            state.ccm.loaded = true;
        },
        setCCMEnrollment(state, action) {
            state.ccm.data = action.payload;
            state.ccm.existingEnrollmentStatus = deriveEnrollmentStatus(action.payload);
            state.ccm.loaded = true;
        },
        setHRMEnrollmentError(state) {
            state.hrm.error = true;
            state.hrm.loaded = true;
        },
        setHRMEnrollment(state, action) {
            state.hrm.data = action.payload;
            state.hrm.existingEnrollmentStatus = deriveEnrollmentStatus(action.payload);
            state.hrm.loaded = true;
        },
        setRPMEnrollmentError(state) {
            state.rpm.error = true;
            state.rpm.loaded = true;
        },
        setRPMEnrollment(state, action) {
            state.rpm.data = action.payload;
            state.rpm.existingEnrollmentStatus = deriveEnrollmentStatus(action.payload);
            state.rpm.loaded = true;
        },
        setCurrentEnrollments(state, action) {
            state.currentEnrollments.data = action.payload;
            state.currentEnrollments.loaded = true;
        },
        setCurrentEnrollmentsError(state) {
            state.currentEnrollments.error = true;
            state.currentEnrollments.loaded = true;
        },

        resetState(state) {
            state.currentEnrollments = initialState.currentEnrollments;
            state.ccm = initialState.ccm;
            state.hrm = initialState.hrm;
            state.rpm = initialState.rpm;
        },

        setEnrollmentDocumentError(state) {
            state.enrollmentDocumentStatus.loaded = true;
            state.enrollmentDocumentStatus.error = true;
        },
        setEnrollmentDocumentStatus(state, action) {
            state.enrollmentDocumentStatus.loaded = true;
            state.enrollmentDocumentStatus.error = false;
            state.enrollmentDocumentStatus.data = action.payload;
        },
        resetEnrollmentDocumentStatus(state) {
            state.enrollmentDocumentStatus = initialState.enrollmentDocumentStatus;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function resetState() {
    return dispatch(slice.actions.resetState());
}

export function resetEnrollmentDocumentStatus() {
    return dispatch(slice.actions.resetEnrollmentDocumentStatus());
}

export function getCCMEnrollment(patientId) {
    return async () => {
        try {
            const response = await enrollmentAPI.getEnrollment(patientId, 'ccm');
            dispatch(slice.actions.setCCMEnrollment(response.data.enrollment));
        } catch (error) {
            dispatch(slice.actions.setCCMEnrollmentError(error));
        }
    };
}

export function getHRMEnrollment(patientId) {
    return async () => {
        try {
            const response = await enrollmentAPI.getEnrollment(patientId, 'hrm');
            dispatch(slice.actions.setHRMEnrollment(response.data.enrollment));
        } catch (error) {
            dispatch(slice.actions.setHRMEnrollmentError(error));
        }
    };
}

export function getRPMEnrollment(patientId) {
    return async () => {
        try {
            const response = await enrollmentAPI.getEnrollment(patientId, 'rpm');
            dispatch(slice.actions.setRPMEnrollment(response.data.enrollment));
        } catch (error) {
            dispatch(slice.actions.setRPMEnrollmentError(error));
        }
    };
}

export function getCurrentEnrollments(patientId) {
    return async () => {
        try {
            const response = await enrollmentAPI.getCurrentEnrollments(patientId);
            dispatch(slice.actions.setCurrentEnrollments(response.data.currentEnrollments));
        } catch (error) {
            dispatch(slice.actions.setCurrentEnrollmentsError(error));
        }
    };
}

export function getEnrollmentDocumentStatus(patientId, enrollmentType) {
    return async () => {
        try {
            const response = await enrollmentAPI.getEnrollmentDocumentStatus(patientId, enrollmentType);
            dispatch(slice.actions.setEnrollmentDocumentStatus(response.data));
        } catch (error) {
            dispatch(slice.actions.setEnrollmentDocumentError(error));
        }
    };
}
